/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import BookingAction from "store/booking/BookingAction";
import {useDebouncedDispatch} from "utils/useDebounceQueue";
import {useTypedSelector} from "helpers/reducers";
import Dates from "utils/Dates";
import {useForm} from "react-hook-form";
import {BookingFilter} from "types/app";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import BookingDateFilterInline from "pages/Front/Booking/components/BookingFilters/BookingDateFilterInline";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import DriversCountFilterNew from "pages/Front/Booking/components/BookingFilters/DriversCountFilterNew";
import BookingFooterSteps from "pages/Front/Booking/components/BookingFooterSteps";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {CURRENCY} from "helpers/config";
import {GAEventName} from "types/entities/specific/GAEventName";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
import {getPackageCategoryName} from "utils/packageUtils";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import {PackageCategory} from "types/enums/PackageCategory";
import {getDriversCountByCategory} from "helpers/helpers";
import {toast} from "react-toastify";

interface Params {
    package_category_id: string;
}

interface TimeslotForDate {
    date: string;
    available: boolean;
}

interface BookingOptionsProps extends RouteComponentProps<Params> {}

const BookingOptions = (props: BookingOptionsProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {debouncedDispatch} = useDebouncedDispatch();
    const {categories} = usePackageCategory();
    const {historyPush} = useNavigation();
    const {sendGenericEvent} = useReactGoogleAnalytics();
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedPackageCategory = useTypedSelector(state => state.bookingState.bookingProcess.filter.category);

    // const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    // const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    // const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const [areTimeslotsAvailable, setAreTimeslotsAvailable] = useState<boolean>(true);

    // const {timeslotsAvailable} = useCustomTimeslotsNew(step => {}, props.history, [scrollRefTimeslots, scrollRefCustomTimeslots, scrollRefConfirmTimeslots]);
    // const [timeslotsForDates, setTimeslotsForDates] = useState<TimeslotForDate[]>([]);

    const defaultValues = bookingFilter || {
        adults: 0,
        children: 0,
        date: Dates.ymd(new Date()),
        hours_count: 0,
        activity_ids: [],
        groupType: [],
    };

    const form = useForm<BookingFilter>({
        defaultValues,
    });
    const nextStepDisabled: boolean = (defaultValues.adults == 0 && defaultValues.children == 0) || !defaultValues.date;

    const onChangeDate = (date: string): void => {
        dispatch(
            BookingAction.setFilter({
                ...bookingFilter,
                date: date,
            })
        );
        dispatch(BookingAction.setDateChosenByCustomer(true));
    };

    const onChangeDriversCount = (type: string, value: number) => {
        if (type === "adults") {
            dispatch(BookingAction.setFilterAdultsCount(value));
        }

        if (type === "children") {
            dispatch(BookingAction.setFilterChildrenCount(value));
        }
    };

    const sendGAData = () => {
        if (!bookingFilter.category) {
            return;
        }

        const categoryEntity = categories.find(item => item.id === bookingFilter.category);
        const price = categoryEntity ? categoryEntity.price * 100 : 0;
        const item: DataLayerItem = {
            item_id: null,
            item_name: getPackageCategoryName(bookingFilter.category),
            quantity: bookingFilter.adults + bookingFilter.children,
            item_variant: "",
            price: price,
            currency: CURRENCY,
        };

        sendGenericEvent(GAEventName.SelectItem, price, [item]);
    };

    const onSubmitHandler = () => {
        let minimum = 0;
        if (bookingFilter.category) {
            minimum = getDriversCountByCategory(bookingFilter.category)["package"].minimum;
            if (bookingFilter.adults + bookingFilter.children < minimum) {
                toast.warning(t("Minimum persons not reached", {persons: minimum}));
                return;
            }
        }

        if (
            bookingFilter.category === PackageCategory.Action ||
            bookingFilter.category === PackageCategory.School ||
            bookingFilter.category === PackageCategory.FerienPass
        ) {
            dispatch(BookingAction.resetSelectedPackageItems());
        }
        sendGAData();
        return historyPush(RouteName.TimeslotsNew, "options");
    };

    // const setTimeslotsForDatesHandler = (
    //     timeslotsForDates: TimeslotForDate[],
    //     setTimeslotsForDates: (timeslotsForDates: TimeslotForDate[]) => void,
    //     date: string,
    //     available: boolean
    // ) => {
    //     //@AI for each date, search and update according to timeslotsAvailable
    //     const updatedTimeslotsForDates = [...timeslotsForDates];
    //     let updatedTimeslotForDate = updatedTimeslotsForDates.find(timeslotForDate => timeslotForDate.date === Dates.ymd(date));
    //
    //     //@AI if the updatedTimeslotForDate is not found, create it and push it to timeslotsForDates
    //     if (updatedTimeslotForDate) {
    //         updatedTimeslotForDate.available = available;
    //     } else {
    //         updatedTimeslotsForDates.push({date: Dates.ymd(date), available: available});
    //     }
    //
    //     setTimeslotsForDates(updatedTimeslotsForDates);
    // };
    //
    // console.log(timeslotsForDates);
    // useEffect(() => {
    //     setTimeslotsForDatesHandler(timeslotsForDates, setTimeslotsForDates, bookingFilter.date, timeslotsAvailable.length > 0);
    // }, [timeslotsAvailable.length, bookingFilter.date]);
    //
    // useEffect(() => {
    //     setAreTimeslotsAvailable(timeslotsAvailable.length > 0);
    // }, [timeslotsAvailable]);

    return (
        <div className={"BookingOptions grid grid-cols-12 gap-x-6 gap-y-6 w-full"}>
            {selectedPackageCategory && (
                <BookingCard className={"col-span-12 md:col-span-6"}>
                    <BookingDateFilterInline
                        disabled={false}
                        register={form.register}
                        setValue={form.setValue}
                        showTitle={true}
                        packageCategory={selectedPackageCategory}
                        todayTimeslotsBookable={areTimeslotsAvailable}
                        onChange={onChangeDate}
                    />
                </BookingCard>
            )}

            <BookingCard className={"col-span-12 md:col-span-6"}>
                <DriversCountFilterNew
                    isMinimalistic={false}
                    register={form.register}
                    selectedPackage={null}
                    setValue={form.setValue}
                    showTitle={true}
                    watch={form.watch}
                    onChange={onChangeDriversCount}
                />
            </BookingCard>
            <BookingFooterSteps nextStepDisabled={nextStepDisabled} onClick={onSubmitHandler} />
        </div>
    );
};
export default withRouter(BookingOptions);
